import { Injectable, ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { InviteVendorModalComponent } from '../../pages/vendor-master/invite-vendor-modal/invite-vendor-modal.component';

@Injectable({
  providedIn: 'root'
})
export class VendorModalService {
  private readonly inviteVendorModalSubject = new Subject<void>();
  private modalComponentRef: ComponentRef<InviteVendorModalComponent> | null = null;
  private readonly isBrowser: boolean;
  
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  
  // Observable that components can subscribe to (for backward compatibility with vendor-list page)
  inviteVendorModal$ = this.inviteVendorModalSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private readonly platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Open the invite vendor modal globally
   * Creates and displays the modal component dynamically
   */
  openInviteVendorModal(): void {
    if (!this.isBrowser) return;

    // If modal is already open, don't create another instance
    if (this.modalComponentRef) {
      console.log('Modal is already open, skipping...');
      return;
    }

    // Create the modal component dynamically
    this.modalComponentRef = createComponent(InviteVendorModalComponent, {
      environmentInjector: this.injector,
    });

    // Attach the component to the application
    this.appRef.attachView(this.modalComponentRef.hostView);

    // Append to document body
    const domElem = (this.modalComponentRef.hostView as any).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    // Subscribe to modal close event to clean up
    this.modalComponentRef.instance.modalClosed.subscribe(() => {
      // Delay cleanup to allow closing animation
      setTimeout(() => {
        this.destroyModal();
      }, 300);
    });

    // Open the modal
    this.modalComponentRef.instance.openModal();

    // Also emit for backward compatibility
    this.inviteVendorModalSubject.next();
  }

  /**
   * Destroy the modal component and clean up
   */
  private destroyModal(): void {
    if (this.modalComponentRef) {
      // Remove the DOM element
      const domElem = (this.modalComponentRef.hostView as any).rootNodes[0] as HTMLElement;
      if (domElem?.parentNode) {
        domElem.parentNode.removeChild(domElem);
      }
      
      // Detach and destroy the component
      this.appRef.detachView(this.modalComponentRef.hostView);
      this.modalComponentRef.destroy();
      this.modalComponentRef = null;
      
      console.log('Modal destroyed and cleaned up');
    }
  }
}

